import { useIntl } from 'react-intl'

import { PricingRuleModel, isSingleFact } from '@marketplace-web/escrow-pricing/escrow-pricing-data'
import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { CountryModel } from '@marketplace-web/country/country-data'
import { CatalogNavigationModel } from '@marketplace-web/catalog/catalog-data'

import useFormatPromotionPeriod from './use-format-promotion-period'
import usePricingRuleConditionsDataFormatter from './use-price-rule-conditions-data-formatter'

type Props = {
  priceRule: PricingRuleModel
  countries?: Array<CountryModel> | null
  catalogs?: Array<CatalogNavigationModel>
}

export type RuleDataRow = { label: string; value: string | Array<string | null> }

const usePricingRuleDataFormatter = ({ priceRule, countries, catalogs }: Props) => {
  const translate = useTranslate('available_discounts')
  const { locale } = useIntl()
  const formattedPeriod = useFormatPromotionPeriod(priceRule.startDate, priceRule.endDate)
  const singleFactConditions = priceRule.conditionTree.all.items.filter(isSingleFact)
  const formattedConditionsData = usePricingRuleConditionsDataFormatter({
    ruleConditions: singleFactConditions,
    currencyCode: priceRule.currency,
    catalogs,
    countries,
  })

  const isFixedAmountDeduction = priceRule.fixedAmount !== null

  const priceRuleData = [
    {
      label: translate('price_rule_data.label.promotion_period'),
      value: formattedPeriod,
    },
    {
      label: translate('price_rule_data.label.min_price'),
      value: isFixedAmountDeduction
        ? formatCurrencyAmount({ amount: '0', currencyCode: priceRule.currency }, locale)
        : null,
    },
    ...formattedConditionsData,
  ]

  return priceRuleData.filter(
    ruleData => ruleData.value !== null && ruleData.value?.length > 0,
  ) as Array<RuleDataRow>
}

export default usePricingRuleDataFormatter
